import { LayerNames } from '@/modules/common/types/layers';

import { FDDM_LAYER_TREE, getLayerDefinition, isLayerDefinition } from './layerTree';
import type { LayerTreeItem } from './layerTree';

const getLayerTreeItemName = (item: LayerTreeItem): LayerNames | undefined => {
  if (isLayerDefinition(item)) return item.name;

  return undefined;
};

const getNamedDescendantLayers = (items: readonly LayerTreeItem[]): LayerNames[] =>
  items.flatMap((item) => {
    const layerName = getLayerTreeItemName(item);
    const childLayers = getNamedDescendantLayers(item.children ?? []);

    return layerName ? [layerName, ...childLayers] : childLayers;
  });

const buildLayerParentMap = (
  items: readonly LayerTreeItem[],
  parent?: LayerNames,
): Map<LayerNames, LayerNames> => {
  const parentMap = new Map<LayerNames, LayerNames>();

  items.forEach((item) => {
    const layerName = getLayerTreeItemName(item);

    if (layerName && parent) {
      parentMap.set(layerName, parent);
    }

    if (item.children) {
      buildLayerParentMap(item.children, layerName ?? parent).forEach((value, key) => {
        parentMap.set(key, value);
      });
    }
  });

  return parentMap;
};

const LAYER_PARENT_MAP = buildLayerParentMap(FDDM_LAYER_TREE);

export const getChildLayers = (layer: LayerNames): LayerNames[] => {
  const definition = getLayerDefinition(layer);
  if (!definition?.children) return [];

  return getNamedDescendantLayers(definition.children);
};

export const getParentLayer = (layer: LayerNames): LayerNames | undefined =>
  LAYER_PARENT_MAP.get(layer);

export const getRelativeLayers = (layer: LayerNames): LayerNames[] => {
  const childLayers = getChildLayers(layer);
  const parentLayer = getParentLayer(layer);

  return [...childLayers, ...(parentLayer ? [parentLayer] : [])];
};

export const getSubLayerNameFromLayer = (layer: LayerNames): LayerNames[] => {
  const childLayers = getChildLayers(layer);
  if (childLayers.length > 0) return childLayers;

  const parentLayer = getParentLayer(layer);
  if (parentLayer) return [parentLayer];

  return [];
};
